import { Injectable } from '@angular/core';
import { CompletionMessage } from '@app-libs/model';
import {
  IMessageFormatter,
  FormattedMessage,
  ProviderMessage,
  MessageValidationResult,
} from './message.interfaces';

/**
 * MessageFormatter - Handles message formatting and transformation
 *
 * This service handles:
 * - Message formatting for display
 * - Message transformation for AI providers
 * - Message validation
 * - Message type conversions
 */
@Injectable({
  providedIn: 'root',
})
export class MessageFormatter implements IMessageFormatter {
  /**
   * Format message for display in the UI
   */
  formatForDisplay(message: CompletionMessage): FormattedMessage {
    if (!message) {
      return {
        id: '',
        content: '',
        role: 'user',
        timestamp: new Date(),
        hasFile: false,
        fileName: undefined,
        isStreaming: false,
        error: undefined,
      };
    }
    return {
      id: message.message_id,
      content: message.content || '',
      role: message.role === 'user' ? 'user' : 'assistant',
      timestamp: new Date(message.created_at || new Date()),
      hasFile: !!message.file_id,
      fileName: message.file_name || message.uploaded_file?.file_name,
      isStreaming: false, // Will be set by UI components when needed
      error: undefined, // Error is not a property of CompletionMessage
    };
  }

  /**
   * Format message for AI provider API
   */
  formatForProvider(message: CompletionMessage): ProviderMessage {
    if (!message) {
      return {
        role: 'user',
        content: '',
        file_id: undefined,
      };
    }
    
    // Use the role directly from the message
    let role: 'user' | 'assistant' | 'system' = 'user';
    
    if (message.role === 'user') {
      role = 'user';
    } else if (message.role === 'assistant' || message.role === 'bot') {
      role = 'assistant';
    } else if (message.role === 'system') {
      role = 'system';
    }

    return {
      role,
      content: message.content || '',
      file_id: message.file_id || undefined,
    };
  }

  /**
   * Transform provider response to message format
   */
  transformFromProvider(providerResponse: any): Partial<CompletionMessage> {
    // Handle different provider response formats
    const content = providerResponse.content || 
                   providerResponse.message?.content || 
                   providerResponse.choices?.[0]?.message?.content || 
                   '';

    const role = providerResponse.role || 
                 providerResponse.message?.role || 
                 providerResponse.choices?.[0]?.message?.role || 
                 'assistant';

    // Extract usage if available
    const usage = providerResponse.usage || 
                  providerResponse.data?.usage || 
                  providerResponse.choices?.[0]?.usage;

    return {
      content,
      role: role === 'user' ? 'user' : 'assistant',
      created_at: new Date().toISOString(),
      metadata: usage ? { usage } : undefined,
    };
  }

  /**
   * Validate message before saving or sending
   */
  validateMessage(message: CompletionMessage): MessageValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check required fields
    if (!message.content && !message.file_id) {
      errors.push('Message must have content or file attachment');
    }

    if (!message.conversation_id) {
      errors.push('Message must belong to a conversation');
    }

    if (!message.role) {
      errors.push('Message must have a role');
    }

    // Check content length
    if (message.content && message.content.length > 32000) {
      errors.push('Message content exceeds maximum length of 32000 characters');
    }

    // Check for warnings
    if (message.content && message.content.length > 16000) {
      warnings.push('Message content is very long and may use many tokens');
    }

    if (!message.workspace_id) {
      warnings.push('Message should have a workspace_id for proper organization');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * Validate message for UI context (before sending to backend)
   * Only validates fields that should be present at UI level
   */
  validateUIMessage(message: Partial<CompletionMessage>): MessageValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check required fields for UI
    if (!message.content && !message.uploaded_file) {
      errors.push('Message must have content or file attachment');
    }

    if (!message.role) {
      errors.push('Message must have a role');
    }

    // Check content length
    if (message.content && message.content.length > 32000) {
      errors.push('Message content exceeds maximum length of 32000 characters');
    }

    // Check for warnings
    if (message.content && message.content.length > 16000) {
      warnings.push('Message content is very long and may use many tokens');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * Format messages array for AI provider context
   */
  formatMessagesForProvider(messages: CompletionMessage[]): ProviderMessage[] {
    return messages
      .filter(msg => msg.content || msg.file_id) // Skip empty messages
      .map(msg => this.formatForProvider(msg));
  }

  /**
   * Extract text content from message (strips formatting)
   */
  extractTextContent(message: CompletionMessage): string {
    if (!message) return '';
    // Simple text extraction - can be enhanced later
    return message.content || '';
  }

  /**
   * Check if message contains code blocks
   */
  hasCodeBlocks(message: CompletionMessage): boolean {
    if (!message) return false;
    return /```[\s\S]*?```/.test(message.content || '');
  }

  /**
   * Check if message contains LaTeX math
   */
  hasMathContent(message: CompletionMessage): boolean {
    if (!message) return false;
    // Check for block math $$...$$ or inline math $...$ with LaTeX-like content
    const content = message.content || '';
    
    // Block math: $$...$$
    if (/\$\$[\s\S]*?\$\$/.test(content)) {
      return true;
    }
    
    // Inline math: $...$ but exclude simple currency patterns
    // Look for typical LaTeX patterns: letters, =, ^, _, \, {, }
    const inlineMathPattern = /\$[^\$]*[a-zA-Z=^_\\{}][^\$]*\$/;
    return inlineMathPattern.test(content);
  }
}
